import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Modal, useAuth, useFormGuard, useLiveRefresh, useDebouncedCallback } from '@byscott-io/corebyscott';
import { useWaldoEvent } from '@byscott-io/where-is-waldo';
import { families } from '../api/client';

export default function FamiliesPage() {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [familyList, setFamilyList] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newFamilyName, setNewFamilyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const loadFamilies = async () => {
    try {
      const response = await families.list();
      setFamilyList(response.data);
    } catch (err) {
      console.error('Failed to load families:', err);
      setError('Failed to load families');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFamilies();
  }, []);

  // Live families (#626): refresh on family/student changes; defer while creating.
  useFormGuard(showCreateModal);
  const refresh = useLiveRefresh(useDebouncedCallback(loadFamilies, 400));
  useWaldoEvent(
    ['family_created', 'family_update', 'family_destroyed', 'student_created', 'student_update', 'student_destroyed'],
    refresh
  );

  const handleCreateFamily = async (e) => {
    e.preventDefault();
    if (!newFamilyName.trim()) return;

    setCreating(true);
    try {
      await families.create({ name: newFamilyName.trim() });
      setNewFamilyName('');
      setShowCreateModal(false);
      loadFamilies();
    } catch (err) {
      console.error('Failed to create family:', err);
      setError('Failed to create family');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Families</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage your families and their students.
          </p>
        </div>
        {isAdmin && (
          <div className="mt-4 sm:mt-0">
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Add Family
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {familyList.length === 0 ? (
        <div className="mt-8 text-center">
          <span className="text-6xl">👨‍👩‍👧‍👦</span>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No families</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating a new family.
          </p>
          {isAdmin && (
            <div className="mt-6">
              <button
                type="button"
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
              >
                Add Family
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-8 bg-white shadow overflow-hidden rounded-lg">
          <ul className="divide-y divide-gray-200">
            {familyList.map((family) => (
              <li key={family.id}>
                <Link
                  to={`/app/families/${family.id}`}
                  className="block hover:bg-gray-50"
                >
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-indigo-600 truncate">
                        {family.name}
                      </p>
                      <div className="ml-2 flex-shrink-0 flex">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                          {family.students_count} student{family.students_count !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Create Family Modal */}
      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Family"
        size="lg"
      >
              <form onSubmit={handleCreateFamily}>
                <div>
                  <div className="mt-4">
                    <label htmlFor="family-name" className="block text-sm font-medium text-gray-700">
                      Family Name
                    </label>
                    <input
                      type="text"
                      id="family-name"
                      value={newFamilyName}
                      onChange={(e) => setNewFamilyName(e.target.value)}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="e.g., Smith Family"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button
                    type="submit"
                    disabled={creating || !newFamilyName.trim()}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:col-start-2 sm:text-sm disabled:opacity-50"
                  >
                    {creating ? 'Creating...' : 'Create'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
      </Modal>
    </div>
  );
}
